import { Intent as BlueprintIntent } from '@blueprintjs/core';
import { AddLarge } from '@carbon/icons-react';
import uniq from 'lodash/uniq';
import * as React from 'react';
import { useParams } from 'react-router-dom';
import {
  Body,
  Button,
  ChipInput,
  InputButton,
  LoadingIcon,
  Modal,
  Selector,
} from 'vet-bones/design-system/components';
import { SelectItem } from 'vet-bones/design-system/utils/select';

import { useAppDispatch, useAppSelector } from 'src/ui/app/hooks';
import {
  SHARE_ENDPOINT_DIALOG_CANCEL,
  SHARE_ENDPOINT_DIALOG_EMAILS,
  SHARE_ENDPOINT_DIALOG_EMAILS_ADD,
  SHARE_ENDPOINT_DIALOG_EMAILS_CHIP_REMOVE,
  SHARE_ENDPOINT_DIALOG_ROLES,
  SHARE_ENDPOINT_DIALOG_ROLES_ITEM,
  SHARE_ENDPOINT_DIALOG_SUBMIT,
} from 'src/ui/constants/testIds';
import { useInvalidateConnection } from 'src/ui/containers/endpoints/utils/invalidate';
import { handleMutationResult } from 'src/ui/containers/organization/hooks/helpers';
import { useServerRoles } from 'src/ui/containers/organization/hooks/serverRoles';
import { queueMessage } from 'src/ui/features/notifications';
import { DialogType, closedDialog } from 'src/ui/features/ui';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import {
  Connection,
  useAddConnectionInvitationsMutation,
} from 'src/ui/graph/types';
import {
  useCurrentConnection,
  useGetLastConnectionIndex,
} from 'src/ui/hooks/connection';
import { useOnError, useOnMutate } from 'src/ui/hooks/graph';
import { isDialogOpenSelector } from 'src/ui/selectors/isDialogOpen';
import * as copy from 'src/ui/templates/copy';
import { isValidEmail } from 'src/ui/utils/isValidEmail';

const isOpenSelector = isDialogOpenSelector(DialogType.SHARE_PERSONAL_ENDPOINT);

export const ShareEndpointDialog: React.VFC = () => {
  const { id = '', org = '' } = useParams<{ id?: string; org?: string }>();
  const dispatch = useAppDispatch();

  const isOpen = useAppSelector(isOpenSelector);
  const lastConnectionIndex = useGetLastConnectionIndex(org);
  const onError = useOnError();
  const onMutate = useOnMutate();

  const { connection } = useCurrentConnection(
    id || (lastConnectionIndex === null ? '' : lastConnectionIndex.toString()),
    org
  );
  const invalidateConnection = useInvalidateConnection(
    '',
    connection?.id,
    connection?.index
  );

  const roleConnections = React.useMemo(
    () => (connection ? [connection as Connection] : []),
    [connection]
  );
  const rolesByConnectionId = useServerRoles(
    roleConnections,
    Boolean(isOpen && connection)
  );
  const rolesData = rolesByConnectionId[connection?.id || ''] || {};
  const roleItems = Array.from(rolesData.roles || []).map((role) => ({
    value: role,
    text: role,
  }));

  const [selectedEmails, setSelectedEmails] = React.useState<string[]>([]);
  const [selectedRoles, setSelectedRoles] = React.useState<string[]>([]);
  const [emailsQuery, setEmailsQuery] = React.useState<string>('');
  const [emailsError, setEmailsError] = React.useState<string | null>(null);

  const {
    isLoading,
    mutate: addConnectionInvitations,
  } = useAddConnectionInvitationsMutation(graphQLClient, {
    onMutate,
    onError,
    onSuccess: (data, vars) => {
      invalidateConnection();

      const didSucceed = handleMutationResult(
        dispatch,
        data.addConnectionInvitations,
        copy.components.endpoints.shareEndpointDialog.notifications.error,
        vars.input.emails,
        (res) => res.email
      );
      if (!didSucceed) {
        return;
      }

      dispatch(
        queueMessage({
          intent: BlueprintIntent.SUCCESS,
          message:
            copy.components.endpoints.shareEndpointDialog.notifications.success,
        })
      );
      handleClose();
    },
  });

  const handleClose = () => {
    dispatch(closedDialog());
    setSelectedEmails([]);
    setSelectedRoles([]);
    setEmailsQuery('');
    setEmailsError(null);
  };

  const handleAddEmails = (newEmails: string[]) => {
    const valid: string[] = [];
    const invalid: string[] = [];
    newEmails
      .map((email) => email.split(/\s*[\s,]\s*/))
      .flat()
      .forEach((email) => {
        if (!email) {
          return;
        }

        if (!isValidEmail(email)) {
          invalid.push(email);
        } else {
          valid.push(email);
        }
      });

    setEmailsError(
      invalid.length
        ? copy.components.endpoints.inputs.email.validation(invalid)
        : null
    );

    if (valid.length) {
      setSelectedEmails((prev) => uniq([...prev, ...valid]));
    }

    setEmailsQuery(invalid.join(' '));
    return false;
  };

  const handleSubmit = () => {
    if (!selectedEmails.length || isLoading || !connection?.id) {
      return;
    }

    addConnectionInvitations({
      input: {
        connection_id: connection.id,
        emails: selectedEmails,
        connection_roles: selectedRoles.length ? selectedRoles : undefined,
      },
    });
  };

  const isSubmitDisabled = !selectedEmails.length || isLoading;

  return (
    <Modal
      canOutsideClickClose={false}
      className="endpoint-dialog"
      footerActions={
        <>
          <Button
            onClick={handleClose}
            size="sm"
            testId={SHARE_ENDPOINT_DIALOG_CANCEL}
            variant="ghost-gray"
          >
            {copy.components.endpoints.shareEndpointDialog.buttons.cancel}
          </Button>
          <Button
            disabled={isSubmitDisabled}
            leftIcon={isLoading ? LoadingIcon : undefined}
            onClick={handleSubmit}
            size="sm"
            testId={SHARE_ENDPOINT_DIALOG_SUBMIT}
            variant="primary-accent"
          >
            {copy.components.endpoints.shareEndpointDialog.buttons.submit}
          </Button>
        </>
      }
      headerSize="lg"
      isOpen={Boolean(isOpen && connection)}
      onClose={handleClose}
      title={copy.components.endpoints.shareEndpointDialog.title}
    >
      <div className="endpoint-dialog-body">
        <Body size="md">
          {copy.components.endpoints.shareEndpointDialog.description}
        </Body>

        <ChipInput
          chipButtonTestId={SHARE_ENDPOINT_DIALOG_EMAILS_CHIP_REMOVE}
          className="endpoint-dialog-chip-input"
          clearable={false}
          helpProps={
            emailsError
              ? {
                  className: 'endpoint-dialog-chip-input-error',
                  text: emailsError,
                }
              : undefined
          }
          inputValue={emailsQuery}
          invalid={Boolean(emailsError)}
          label={copy.components.endpoints.inputs.email.label}
          onAdd={handleAddEmails}
          onInputChange={(e) => setEmailsQuery(e.currentTarget.value)}
          onRemove={(_, idx) => {
            setSelectedEmails((prev) => {
              const next = [...prev];
              next.splice(idx, 1);
              return next;
            });
            setEmailsError(null);
          }}
          rightElement={
            emailsQuery.trim() ? (
              <InputButton
                icon={AddLarge}
                onClick={() => handleAddEmails([emailsQuery])}
                testId={SHARE_ENDPOINT_DIALOG_EMAILS_ADD}
              />
            ) : undefined
          }
          size="md"
          testId={SHARE_ENDPOINT_DIALOG_EMAILS}
          values={selectedEmails}
        />

        <Selector.Multi<null, SelectItem, SelectItem[]>
          chipInputProps={{
            className: 'endpoint-dialog-chip-selector',
            clearable: false,
            label: copy.components.endpoints.inputs.roles.shareLabel,
            size: 'md',
            testId: SHARE_ENDPOINT_DIALOG_ROLES,
            values: selectedRoles,
          }}
          creatable
          createText={copy.components.endpoints.inputs.roles.create}
          filterable
          items={roleItems}
          itemTestId={SHARE_ENDPOINT_DIALOG_ROLES_ITEM}
          loading={rolesData.isLoading}
          onSelect={(selected) =>
            setSelectedRoles(selected.map((item) => item.value))
          }
          popoverProps={{
            portalClassName: 'endpoint-dialog-menu-portal',
          }}
          selected={selectedRoles.map((role) => ({ value: role, text: role }))}
        />
      </div>
    </Modal>
  );
};
